import {LocalStorageKeyType, LocalStorageKeysEnum} from '../../core/models/local-storage-keys';
import {LocalStorageService} from '../../core/service/local-storage.service';

/**
 * Local-storage helpers for FE-95 login warnings about DISABLED stores.
 * Acknowledged store IDs are stored per user so only newly disabled stores
 * surface on subsequent logins.
 */

/**
 * Builds the per-user local storage key for acknowledged disabled store IDs.
 */
export function getAcknowledgedDisabledStoresStorageKey(
  localStorageService: LocalStorageService,
  userId: string,
): string {
  return localStorageService.generateLocalStorageKey(
    LocalStorageKeysEnum.acknowledgedDisabledStores,
    userId,
  );
}

/**
 * Reads acknowledged disabled store IDs for the given user.
 */
export function readAcknowledgedDisabledStoreIds(
  localStorageService: LocalStorageService,
  userId: string,
): string[] {
  const key: string = getAcknowledgedDisabledStoresStorageKey(localStorageService, userId);
  const stored: string[] | null = localStorageService.getLocalStorageItem<string[]>(key);
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter((storeId: string): boolean => typeof storeId === 'string' && storeId.length > 0);
}

/**
 * Writes the full acknowledged disabled store ID list for the given user.
 */
export function writeAcknowledgedDisabledStoreIds(
  localStorageService: LocalStorageService,
  userId: string,
  storeIds: string[],
): string[] {
  const key: string = getAcknowledgedDisabledStoresStorageKey(localStorageService, userId);
  const uniqueIds: string[] = Array.from(new Set(storeIds.filter((storeId: string): boolean => !!storeId)));
  localStorageService.setLocalStorageItem(key as LocalStorageKeyType, uniqueIds);
  return uniqueIds;
}

/**
 * Drops acknowledged IDs that are no longer DISABLED (re-enabled or gone).
 * Only IDs still present in `currentlyDisabledIds` are kept.
 */
export function pruneAcknowledgedDisabledStoreIds(
  localStorageService: LocalStorageService,
  userId: string,
  currentlyDisabledIds: string[],
): string[] {
  const acknowledged: string[] = readAcknowledgedDisabledStoreIds(localStorageService, userId);
  const stillDisabled = new Set<string>(currentlyDisabledIds);
  const pruned: string[] = acknowledged.filter((storeId: string): boolean => stillDisabled.has(storeId));
  const removedIds: string[] = acknowledged.filter((storeId: string): boolean => !stillDisabled.has(storeId));

  if (removedIds.length > 0 || pruned.length !== acknowledged.length) {
    writeAcknowledgedDisabledStoreIds(localStorageService, userId, pruned);
  }

  return pruned;
}

/**
 * Merges newly acknowledged disabled store IDs into local storage.
 * Always drops IDs that are no longer in `currentlyDisabledIds` when that list is provided.
 */
export function acknowledgeDisabledStoreIds(
  localStorageService: LocalStorageService,
  userId: string,
  newlyAcknowledgedIds: string[],
  currentlyDisabledIds?: string[],
): void {
  const existing: string[] = readAcknowledgedDisabledStoreIds(localStorageService, userId);
  const merged: string[] = Array.from(new Set([...existing, ...newlyAcknowledgedIds]));
  const pruned: string[] = currentlyDisabledIds
    ? merged.filter((storeId: string): boolean => currentlyDisabledIds.includes(storeId))
    : merged;
  writeAcknowledgedDisabledStoreIds(localStorageService, userId, pruned);
}

/**
 * Returns disabled stores that have not yet been acknowledged for this user.
 * Re-enabled stores are removed from local storage first (only still-disabled IDs remain).
 */
export function getUnacknowledgedDisabledStores<T extends {storeId: string}>(
  localStorageService: LocalStorageService,
  userId: string,
  disabledStores: T[],
): T[] {
  const currentlyDisabledIds: string[] = disabledStores.map((store: T) => store.storeId);
  const prunedAcknowledged: string[] = pruneAcknowledgedDisabledStoreIds(
    localStorageService,
    userId,
    currentlyDisabledIds,
  );
  const acknowledgedSet = new Set<string>(prunedAcknowledged);
  return disabledStores.filter((store: T): boolean => !acknowledgedSet.has(store.storeId));
}
